// ui/components/debug/DashboardTodosDebugTab.tsx
// ✅ 覆蓋全文
//
// 目的：Debug 儀錶板三人待辦計數（client 即時計算 vs server KPI）
// - 重點：任何 Firebase offline / schema 差異都不應讓整頁爆紅。
// - 本 Tab 以「可用為主」：一律 try/catch，顯示錯誤訊息即可。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getTrueTodosOnce, type TrueTodosResult } from "../../services/trueTodoAggregator";
import { computeTrueDashboardSummary } from "../../services/todosSummary";
import type { DailySummaryResult } from "../../services/dailySummaryService";
import { getTrueTodoKpiSummaryOnce, triggerRebuildTrueTodoKpiSummaryNow } from "../../services/trueTodoKpiSummaryService";

type FetchState<T> = { loading: boolean; value: T | null; error: string | null };

function pretty(v: any): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export const DashboardTodosDebugTab: React.FC = () => {
  const [client, setClient] = useState<FetchState<{ res: TrueTodosResult; summary: DailySummaryResult }>>({
    loading: true,
    value: null,
    error: null,
  });

  const [server, setServer] = useState<FetchState<any>>({ loading: true, value: null, error: null });

  const loadClient = useCallback(async () => {
    setClient({ loading: true, value: null, error: null });
    try {
      const res = await getTrueTodosOnce(400);
      const summary = computeTrueDashboardSummary(res.trueTodos);
      setClient({ loading: false, value: { res, summary }, error: null });
    } catch (e: any) {
      setClient({ loading: false, value: null, error: String(e?.message ?? e) });
    }
  }, []);

  const loadServer = useCallback(async () => {
    setServer({ loading: true, value: null, error: null });
    try {
      const s = await getTrueTodoKpiSummaryOnce();
      setServer({ loading: false, value: s, error: null });
    } catch (e: any) {
      setServer({ loading: false, value: null, error: String(e?.message ?? e) });
    }
  }, []);

  useEffect(() => {
    void loadClient();
    void loadServer();
  }, [loadClient, loadServer]);

  const clientBrief = useMemo(() => {
    if (!client.value) return null;
    const { summary, res } = client.value;
    return {
      source: summary?.debug?.raw?.source ?? "unknown",
      trueTodos: res.trueTodos?.length ?? 0,
      excluded: res.excluded?.length ?? 0,
      users: (summary.users ?? []).map((u) => ({
        userKey: u.userKey,
        displayName: u.displayName,
        total: u.counts?.total ?? 0,
        contractReviews: u.counts?.contractReviews ?? 0,
        templates: u.counts?.templates ?? 0,
        seals: u.counts?.seals ?? 0,
        cases: u.counts?.cases ?? 0,
        govCorrespondence: u.counts?.govCorrespondence ?? 0,
        regIntel: u.counts?.regIntel ?? 0,
        projectControl: u.counts?.projectControl ?? 0,
        governanceWorkspace: u.counts?.governanceWorkspace ?? 0,
      })),
    };
  }, [client.value]);

  const onRebuildServer = useCallback(async () => {
    try {
      const r = await triggerRebuildTrueTodoKpiSummaryNow();
      // best-effort: reload server after
      await loadServer();
      alert(r?.ok ? "✅ 已觸發重建" : `⚠️ 未觸發：${r?.message ?? "unknown"}`);
    } catch (e: any) {
      alert(`❌ 重建失敗：${String(e?.message ?? e)}`);
    }
  }, [loadServer]);

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={loadClient} disabled={client.loading}>
          {client.loading ? "載入中…" : "重新載入（Client）"}
        </button>
        <button onClick={loadServer} disabled={server.loading}>
          {server.loading ? "載入中…" : "重新載入（Server）"}
        </button>
        <button onClick={onRebuildServer}>重建伺服器 KPI</button>
      </div>

      <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
        <h3 style={{ margin: "0 0 8px 0" }}>Client｜即時計算（三人待辦）</h3>

        {client.error ? (
          <div style={{ color: "crimson" }}>Error: {client.error}</div>
        ) : client.loading ? (
          <div>載入中…</div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: 13 }}>
              source: <b>{String(clientBrief?.source ?? "unknown")}</b> / trueTodos:{" "}
              <b>{clientBrief?.trueTodos ?? 0}</b> / excluded: <b>{clientBrief?.excluded ?? 0}</b>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{pretty(clientBrief)}</pre>
          </>
        )}
      </section>

      <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
        <h3 style={{ margin: "0 0 8px 0" }}>Server｜KPI Summary（dashboard_todos_kpi_summary）</h3>

        {server.error ? (
          <div style={{ color: "crimson" }}>Error: {server.error}</div>
        ) : server.loading ? (
          <div>載入中…</div>
        ) : (
          <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{pretty(server.value)}</pre>
        )}
      </section>
    </div>
  );
};

export default DashboardTodosDebugTab;
